import {
  Box,
  Icon,
  Spinner,
  Table,
  Tag,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'
import isNil from 'lodash/isNil'

import { observer } from 'mobx-react'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { convertDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import {
  COLUMN_SPANS,
  TOTAL_COLUMNS_SPAN,
} from 'modules/automations/components/AutomationHistory/entries_table/constants'
import { ExecutionHistoryEntryState } from 'modules/automations/lib/enums'
import {
  ExecutionHistoryAggregateAction,
  ExecutionHistoryAggregateAutomation,
  type ExecutionHistoryEntry,
} from 'modules/automations/store/models/ExecutionHistoryEntry'
import type CustomFieldsCollection from 'modules/custom_resources/store/collections/customFields/fields'

import HistoryAction from './action'

interface StateBadgeProps {
  state: ExecutionHistoryEntryState
}

function StateBadge({ state }: StateBadgeProps) {
  switch (state) {
    case ExecutionHistoryEntryState.Running:
      return <Tag type="champagne">{i18n.t('automations.history.execution_state.running')}</Tag>
    case ExecutionHistoryEntryState.Stopped:
      return (
        <Tag type="radical">
          <Box inline alignItems="center" justifyContent="spaceBetween" flexDirection="row">
            <Icon.Small icon="info" encircled="radicalRed1000" />
            <Box paddingLeft="s6">{i18n.t('automations.history.execution_state.stopped')}</Box>
          </Box>
        </Tag>
      )
    case ExecutionHistoryEntryState.Finished:
      return <Tag type="lime">{i18n.t('automations.history.execution_state.finished')}</Tag>
    case ExecutionHistoryEntryState.Error:
      return (
        <Tag type="radical" solid>
          {i18n.t('automations.history.execution_state.error')}
        </Tag>
      )
    default:
      return <Tag type="neutral">{state}</Tag>
  }
}

/**
 * Get the root actions:
 * - The actions without a parent (parent_id === undefined)
 * - The actions with a parent that is not in the list of actions.
 *   It may happen if an action/condition is hidden, like time actions
 */
function getFirstActions(
  actions: ExecutionHistoryAggregateAction[],
  actionsByParentId: Map<string | undefined, ExecutionHistoryAggregateAction[]>
) {
  const actionIds = new Set(actions.map((action) => action.id))

  const firstActions = [...actionsByParentId.keys()]
    .filter((parentId) => isNil(parentId) || !actionIds.has(parentId))
    .flatMap((parentId) => actionsByParentId.get(parentId) ?? [])

  if (firstActions.length === 0) {
    throw new Error('Automation without a root action')
  }

  return firstActions
}

/**
 *  Sort the actions by hierarchy, using the parent_id to chain them
 */
function getSortedActions(
  aggregate: ExecutionHistoryAggregateAutomation
): ExecutionHistoryAggregateAction[] {
  const actions = Object.values(aggregate.actions)

  if (actions.length <= 1) {
    return actions
  }

  const sortedActions: ExecutionHistoryAggregateAction[] = []
  // A lookup map for easier access
  const actionsByParentId = new Map<string | undefined, ExecutionHistoryAggregateAction[]>()

  // Fill the lookup map
  actions.forEach((action) => {
    const parentId = action.parent_id ?? undefined

    if (isNil(actionsByParentId.get(parentId))) {
      actionsByParentId.set(parentId, [action])
    } else {
      actionsByParentId.get(parentId)?.push(action)
    }
  })

  const firstActions = getFirstActions(actions, actionsByParentId)

  sortedActions.push(...firstActions)

  const nextParents = new Set(firstActions)
  // To break loops
  const visitedActions = new Set(firstActions)

  // Walk over the actions from parents to children
  while (nextParents.size > 0) {
    const currentParents = [...nextParents]
    nextParents.clear()

    currentParents.forEach((parent) => {
      const children = actionsByParentId
        .get(parent.id)
        ?.filter((action) => !visitedActions.has(action))

      if (isNil(children)) {
        return
      }

      sortedActions.push(...children)
      children.forEach((child) => nextParents.add(child))
    })
  }

  return sortedActions
}

interface HistoryEntryRowProps {
  historyEntry: ExecutionHistoryEntry
  customFieldsCollection: CustomFieldsCollection
}

const HistoryEntryRow = ({ historyEntry, customFieldsCollection }: HistoryEntryRowProps) => {
  const [open] = useToggle(false)
  const [openAlert, toggleAlert] = useToggle(false)

  const { value, success } = useAsyncAction(async () => historyEntry.fetchAggregate(), {
    successFlash: false,
  })

  const content = () => {
    const contentState = historyEntry.state
    const tooltipDescription = i18n.t('automations.show.history.stopped_error')
    return (
      <>
        <Table.Cell colSpan={COLUMN_SPANS[0]}>
          <Box inline>
            <Tooltip.Alt
              attachment="bottom center"
              open={openAlert && contentState === ExecutionHistoryEntryState.Stopped}
              toggle={toggleAlert}
              label={tooltipDescription}
            >
              <StateBadge state={contentState} />
            </Tooltip.Alt>
          </Box>
        </Table.Cell>
        <Table.Cell colSpan={COLUMN_SPANS[1]}>
          <Text.Normal weight="medium">
            {convertDate(historyEntry.startedAt, 'backend_datetime', 'ui_short_with_time')}
          </Text.Normal>
        </Table.Cell>
      </>
    )
  }

  const toggleContent = useUnsafeCallback(() => {
    // Expansion is disabled — no-op
  }, [])

  const aggregate = success ? (value as ExecutionHistoryAggregateAutomation) : undefined
  return (
    <Table.ExpandableRow
      content={content}
      toggle={toggleContent}
      opened={open}
      iconColSpan={COLUMN_SPANS[2]}
      icon="chevron"
      iconPosition="right"
      isExpandable={false}
    >
      <Table.Row>
        <Table.Cell colSpan={TOTAL_COLUMNS_SPAN} isExplotable>
          {aggregate ? (
            <Box flexDirection="column" gap="s4">
              {getSortedActions(aggregate).map((action) => (
                <HistoryAction
                  key={`${action.id}`}
                  aggregateAction={action}
                  customFieldsCollection={customFieldsCollection}
                />
              ))}
            </Box>
          ) : (
            <Box width="full" alignItems="center">
              <Spinner />
            </Box>
          )}
        </Table.Cell>
      </Table.Row>
    </Table.ExpandableRow>
  )
}

export default observer(HistoryEntryRow)
